import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import * as Sentry from '@sentry/vue';
import { crashlytics } from '@/services/crashlytics';
import EventBus from '@/services/eventBus';

const isNative = Capacitor.isNativePlatform();

// EventBus events driving App.vue's full-screen overlay when a required update
// is discovered after the initial startup check has completed. The mandatory
// startup check uses a raw-DOM overlay because Vue is intentionally not mounted
// until Capgo confirms that this bundle is current.
export const LIVE_UPDATE_DOWNLOADING_EVENT = 'live-update-downloading';
export const LIVE_UPDATE_PROGRESS_EVENT = 'live-update-progress';
export const LIVE_UPDATE_DISMISS_EVENT = 'live-update-dismiss';

// localStorage key recording the bundle id we last tried to apply via reload().
// Used to detect a rollback/re-stage loop: if Capgo rolls a freshly-applied
// bundle back (it failed to notifyAppReady in time) and then re-downloads/re-stages
// the SAME bundle, we would reload into it again and loop forever. This marker
// lets us reload a given id at most once until a launch proves healthy.
const PENDING_APPLY_KEY = 'ko.liveUpdate.pendingApplyId';

// localStorage key counting consecutive download attempts that started but never
// completed (see download-abort telemetry below). A user who repeatedly opens and
// immediately closes the app aborts the in-process zip download every time, so it
// never finishes, never fires updateAvailable, and never applies — the device stays
// silently stuck on a stale bundle. This counter makes that pattern visible and lets
// us escalate the report severity once it recurs. Reset to 0 on any completed
// download (success or definitive "nothing to do").
const ABORTED_DOWNLOAD_COUNT_KEY = 'ko.liveUpdate.abortedDownloadCount';

// After this many consecutive aborted downloads we treat the device as genuinely
// stuck and report at error level rather than as a warning breadcrumb.
const ABORTED_DOWNLOAD_ALERT_THRESHOLD = 3;

// localStorage key recording when we last confirmed a promoted bundle booted
// successfully (see notifyCurrentBundleReady). Persists across the reload an
// install triggers, so the freshly-booted session can tell it just finished
// installing rather than treating this like any other cold start.
const LAST_INSTALL_KEY = 'ko.liveUpdate.lastInstalledAt';

// Skip the update check entirely if we installed an update this recently. The
// reload after a successful install is itself a fresh app launch, which would
// otherwise immediately re-trigger the mandatory check against the server we
// just synced with.
const RECENT_INSTALL_COOLDOWN_MS = 15000;

// Native setup calls should finish quickly; this catches a wedged initialization
// before the authoritative update check starts. Once checking begins we wait for
// Capgo's terminal event without a client timeout because those events carry no
// request ID and cannot safely be correlated to a replacement attempt.
const UPDATE_STALL_TIMEOUT_MS = 30000;
const MINIMUM_UPDATE_SCREEN_MS = 1000;

// In-memory record of a download in progress THIS session. Set when `download`
// first fires, cleared when the download reaches a terminal state
// (downloadComplete/updateAvailable/downloadFailed/noNeedUpdate). If the app goes
// to background or the page is hidden while this is still set, the download was
// aborted — that is the silent failure mode we report.
let activeDownload = null;

// Mandatory startup-gate state. The promise resolves only after Capgo emits
// noNeedUpdate. Available bundles reload the webview; errors leave this promise
// pending and expose Retry, so native gameplay can never start unverified.
let startupGatePromise = null;
let resolveStartupGate = null;
let startupGatePending = false;
let startupGateCompleting = false;
let updateScreenShownAt = null;
let checkInProgress = false;
let applyingBundle = false;
let checkWatchdog = null;
let initializationAttempt = 0;
let updateCheckAttempt = 0;
let acceptingStartupCheckResult = false;
let appIsActive = true;

// Listener handles also act as registration guards so retries cannot bind the
// same native event more than once.
const updaterListenerHandles = {};
const updaterListenerPromises = {};
let appStateListenerHandle = null;
let appStateListenerPromise = null;
let pageHideListenerRegistered = false;

function readAbortedDownloadCount() {
    try {
        const raw = window.localStorage.getItem(ABORTED_DOWNLOAD_COUNT_KEY);
        const n = raw == null ? 0 : parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
        return 0;
    }
}

function writeAbortedDownloadCount(n) {
    try {
        window.localStorage.setItem(ABORTED_DOWNLOAD_COUNT_KEY, String(n));
    } catch {
        // Storage unavailable: telemetry is best-effort, fall through.
    }
}

function clearAbortedDownloadCount() {
    try {
        window.localStorage.removeItem(ABORTED_DOWNLOAD_COUNT_KEY);
    } catch {
        // no-op
    }
}

function readLastInstalledAt() {
    try {
        const raw = window.localStorage.getItem(LAST_INSTALL_KEY);
        const n = raw == null ? null : parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

function writeLastInstalledAt(timestamp) {
    try {
        window.localStorage.setItem(LAST_INSTALL_KEY, String(timestamp));
    } catch {
        // Storage unavailable: worst case we lose the cooldown, same as before
        // this guard existed.
    }
}

function recentlyInstalledUpdate() {
    const lastInstalledAt = readLastInstalledAt();
    return lastInstalledAt !== null && (Date.now() - lastInstalledAt) < RECENT_INSTALL_COOLDOWN_MS;
}

function readPendingApplyId() {
    try {
        return window.localStorage.getItem(PENDING_APPLY_KEY);
    } catch {
        return null;
    }
}

function writePendingApplyId(id) {
    try {
        window.localStorage.setItem(PENDING_APPLY_KEY, id);
    } catch {
        // Storage unavailable: fall through. Worst case we lose loop protection,
        // which is no worse than before this guard existed.
    }
}

function clearPendingApplyId() {
    try {
        window.localStorage.removeItem(PENDING_APPLY_KEY);
    } catch {
        // no-op
    }
}

function markUpdateScreenShown() {
    if (updateScreenShownAt === null) {
        updateScreenShownAt = Date.now();
    }
}

async function waitForMinimumUpdateScreenDuration() {
    if (updateScreenShownAt === null) return;
    const remainingMs = MINIMUM_UPDATE_SCREEN_MS - (Date.now() - updateScreenShownAt);
    if (remainingMs > 0) {
        await new Promise(resolve => window.setTimeout(resolve, remainingMs));
    }
}

function ensureUpdateScreen() {
    markUpdateScreenShown();
    let overlay = document.getElementById('live-update-overlay');
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = 'live-update-overlay';
    overlay.setAttribute('style', [
        'position: fixed',
        'inset: 0',
        'z-index: 999999',
        'display: flex',
        'flex-direction: column',
        'align-items: center',
        'justify-content: center',
        'background-color: #0E0228',
        'background-image: url("/assets/update-splash.webp")',
        'background-position: center',
        'background-size: cover',
        'background-repeat: no-repeat',
        'color: #ffffff',
        'font-family: system-ui, sans-serif',
        'text-align: center',
        'padding: 24px',
        'box-sizing: border-box',
    ].join(';'));

    overlay.innerHTML = `
        <div style="
            width: 100%;
            max-width: 340px;
            padding: 24px;
            border-radius: 16px;
            background: rgba(14, 2, 40, 0.82);
            box-shadow: 0 12px 36px rgba(0, 0, 0, 0.45);
            backdrop-filter: blur(4px);
            box-sizing: border-box;
        ">
            <div data-live-update-spinner style="
                width: 40px;
                height: 40px;
                border: 3px solid rgba(255,255,255,0.2);
                border-top-color: #ffffff;
                border-radius: 50%;
                animation: ko-spin 0.8s linear infinite;
                margin: 0 auto 20px;
            "></div>
            <p data-live-update-title style="margin: 0; font-size: 18px; font-weight: 600;"></p>
            <p data-live-update-message style="margin: 8px 0 0; color: rgba(255,255,255,0.72); font-size: 14px; line-height: 1.4;"></p>
            <p data-live-update-progress style="display: none; margin: 12px 0 0; color: #A8B8CC; font-size: 14px; font-variant-numeric: tabular-nums;"></p>
            <button data-live-update-retry type="button" style="
                display: none;
                margin: 22px auto 0;
                min-width: 120px;
                padding: 11px 20px;
                border: 0;
                border-radius: 6px;
                background: #ffffff;
                color: #0E0228;
                font: inherit;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
            ">Retry</button>
        </div>
        <style>
            @keyframes ko-spin {
                to { transform: rotate(360deg); }
            }

            @media (max-width: 599px) and (orientation: portrait) {
                #live-update-overlay {
                    background-image: url("/assets/update-splash-phone.webp") !important;
                    background-position: center top !important;
                    background-size: cover !important;
                }
            }
        </style>
    `;

    (document.body ?? document.documentElement).appendChild(overlay);
    return overlay;
}

function renderUpdateScreen({ title, message, percent = null, retry = null, spinning = true }) {
    const overlay = ensureUpdateScreen();
    const spinner = overlay.querySelector('[data-live-update-spinner]');
    const titleElement = overlay.querySelector('[data-live-update-title]');
    const messageElement = overlay.querySelector('[data-live-update-message]');
    const progressElement = overlay.querySelector('[data-live-update-progress]');
    const retryButton = overlay.querySelector('[data-live-update-retry]');

    spinner.style.display = spinning ? 'block' : 'none';
    titleElement.textContent = title;
    messageElement.textContent = message;

    if (typeof percent === 'number') {
        progressElement.style.display = 'block';
        progressElement.textContent = `${Math.round(percent)}%`;
    } else {
        progressElement.style.display = 'none';
        progressElement.textContent = '';
    }

    retryButton.onclick = null;
    retryButton.disabled = false;
    retryButton.style.opacity = '1';
    if (retry) {
        retryButton.style.display = 'block';
        retryButton.onclick = async () => {
            retryButton.disabled = true;
            retryButton.style.opacity = '0.65';
            try {
                await retry();
            } catch (error) {
                showBlockingUpdateError(
                    'Unable to check for required updates. Check your connection and retry.',
                    error,
                    retry,
                );
            }
        };
    } else {
        retryButton.style.display = 'none';
    }
}

function hideUpdateScreen() {
    document.getElementById('live-update-overlay')?.remove();
    updateScreenShownAt = null;
}

function showCheckingScreen() {
    renderUpdateScreen({
        title: 'Checking for updates…',
        message: 'The latest version is required before Knockout can open.',
    });
}

async function revealUpdateScreen() {
    // Give the raw DOM a paint opportunity, then hide the plugin splash early.
    // launchAutoHide remains enabled as a native fallback if hide() rejects.
    await new Promise(resolve => window.requestAnimationFrame(() => resolve()));
    try {
        await SplashScreen.hide();
    } catch (error) {
        reportUpdateFailure('Native splash release failed; using auto-hide fallback', error);
    }
}

function showDownloadScreen(percent = 0) {
    markUpdateScreenShown();
    if (startupGatePending) {
        renderUpdateScreen({
            title: 'Installing required update…',
            message: 'Please keep Knockout open while the latest version is downloaded.',
            percent,
        });
        return;
    }

    EventBus.emit(LIVE_UPDATE_DOWNLOADING_EVENT, percent);
    EventBus.emit(LIVE_UPDATE_PROGRESS_EVENT, percent);
}

function clearCheckWatchdog() {
    if (checkWatchdog !== null) {
        window.clearTimeout(checkWatchdog);
        checkWatchdog = null;
    }
}

function reportUpdateFailure(context, error) {
    const detail = error?.message ?? String(error ?? 'unknown error');
    const message = `${context}: ${detail}`;
    Sentry.captureException(error instanceof Error ? error : new Error(message));
    crashlytics.recordException(message);
}

function showBlockingUpdateError(message, error, retry) {
    clearCheckWatchdog();
    checkInProgress = false;
    acceptingStartupCheckResult = false;
    updateCheckAttempt += 1;
    applyingBundle = false;
    EventBus.emit(LIVE_UPDATE_DISMISS_EVENT);
    reportUpdateFailure('Required live update failed', error);
    renderUpdateScreen({
        title: 'Update required',
        message,
        retry,
        spinning: false,
    });
}

function armCheckWatchdog(retry, onTimeout = null) {
    clearCheckWatchdog();
    checkWatchdog = window.setTimeout(() => {
        onTimeout?.();
        showBlockingUpdateError(
            'The update check is taking too long. Check your connection and retry.',
            new Error(`Required live update stalled for ${UPDATE_STALL_TIMEOUT_MS}ms`),
            retry,
        );
    }, UPDATE_STALL_TIMEOUT_MS);
}

// Shared promotion primitive: show the overlay, let it paint, record the
// anti-reloop marker for the bundle we are about to boot into, then reload.
async function promoteAndReload(bundleId) {
    if (bundleId) {
        writePendingApplyId(bundleId);
    }

    renderUpdateScreen({
        title: 'Applying update…',
        message: 'Knockout will restart automatically when the update is ready.',
    });

    // Keep the splash visible for at least two seconds from the first checking
    // or download frame, then allow one final paint before reloading.
    await waitForMinimumUpdateScreenDuration();
    await new Promise(resolve => setTimeout(resolve, 100));
    await CapacitorUpdater.reload();

    // A successful reload tears down this document. If the plugin returns but the
    // document remains alive, fail closed and offer Retry rather than mounting.
    await new Promise((resolve, reject) => {
        window.setTimeout(() => reject(new Error('Capgo reload returned without restarting the app')), 10000);
    });
}

// Record that a download is underway this session. Called on the first `download`
// event. `version` is best-effort context for the eventual abort report.
function markDownloadStarted(version, percent) {
    if (!activeDownload) {
        activeDownload = { version: version ?? null, percent: percent ?? 0, startedAt: Date.now() };
    } else {
        activeDownload.percent = percent ?? activeDownload.percent;
        if (version) activeDownload.version = version;
    }
}

// A download reached a terminal state (complete, applied, failed, or nothing to
// do). Clear the in-progress marker so a later background does not misreport it as
// aborted, and reset the consecutive-abort counter.
function markDownloadSettled() {
    activeDownload = null;
    clearAbortedDownloadCount();
}

// Called when the app leaves the foreground (background/inactive) or the page is
// hidden. If a download was in progress and never settled, it is being aborted by
// process teardown. Report it and escalate severity when it recurs.
function reportDownloadAbortIfInFlight(reason) {
    if (!activeDownload) return;

    const { version, percent, startedAt } = activeDownload;
    activeDownload = null;

    const count = readAbortedDownloadCount() + 1;
    writeAbortedDownloadCount(count);

    const elapsedMs = startedAt ? Date.now() - startedAt : null;
    const message = `Live update download aborted (${reason}) at ${percent ?? 0}% for bundle ${version ?? 'unknown'}; consecutive aborted downloads: ${count}.`;

    crashlytics.recordException(message);
    Sentry.captureEvent({
        message,
        level: count >= ABORTED_DOWNLOAD_ALERT_THRESHOLD ? 'error' : 'warning',
        tags: {
            live_update_event: 'download_aborted',
            live_update_abort_reason: reason,
        },
        extra: {
            bundleVersion: version ?? null,
            percent: percent ?? 0,
            elapsedMs,
            consecutiveAbortedDownloads: count,
        },
    });
}

async function notifyCurrentBundleReady() {
    const current = await CapacitorUpdater.notifyAppReady();
    const runningId = current?.bundle?.id ?? current?.id ?? null;
    if (runningId && readPendingApplyId() === runningId) {
        clearPendingApplyId();
        // This boot is running the bundle we promoted+reloaded into last time,
        // and it just proved itself ready — that's the successful-install
        // moment. Record it so the check we're about to run can be skipped.
        writeLastInstalledAt(Date.now());
    }
}

async function completeStartupGate() {
    if (startupGateCompleting) return;
    startupGateCompleting = true;
    clearCheckWatchdog();
    checkInProgress = false;
    acceptingStartupCheckResult = false;
    updateCheckAttempt += 1;
    markDownloadSettled();
    EventBus.emit(LIVE_UPDATE_DISMISS_EVENT);

    await waitForMinimumUpdateScreenDuration();

    // Deliberately NOT calling hideUpdateScreen() here. Removing the raw-DOM
    // overlay now would leave a blank gap: this promise resolving only lets
    // main.js START app.mount(), which still needs its own tick(s) to run and
    // paint App.vue's "Connecting…" overlay on top before anything is actually
    // covering the screen. The overlay is kept up across that handoff and only
    // torn down by main.js, via liveUpdates.hideStartupOverlay(), once Vue's
    // mount has had a frame to paint.

    if (startupGatePending) {
        startupGatePending = false;
        const resolve = resolveStartupGate;
        resolveStartupGate = null;
        resolve?.();
    }

    startupGateCompleting = false;
}

async function installBundle(bundleId, { stage = true, retryingFailedBundle = false } = {}) {
    if (!bundleId || applyingBundle) return;

    if (readPendingApplyId() === bundleId && !retryingFailedBundle) {
        const error = new Error(`Bundle ${bundleId} previously failed to become ready and was rolled back`);
        showBlockingUpdateError(
            'The required update could not start correctly. Retry the installation.',
            error,
            () => installBundle(bundleId, { stage, retryingFailedBundle: true }),
        );
        return;
    }

    applyingBundle = true;
    checkInProgress = false;
    clearCheckWatchdog();
    markDownloadSettled();

    if (retryingFailedBundle) {
        clearPendingApplyId();
    }

    try {
        if (stage) {
            await CapacitorUpdater.next({ id: bundleId });
        }
        await promoteAndReload(bundleId);
    } catch (error) {
        applyingBundle = false;
        showBlockingUpdateError(
            'The required update could not be installed. Check your connection and retry.',
            error,
            () => installBundle(bundleId, { stage, retryingFailedBundle: true }),
        );
    }
}

async function addUpdaterListenerOnce(eventName, callback) {
    if (updaterListenerHandles[eventName]) return;
    if (!updaterListenerPromises[eventName]) {
        updaterListenerPromises[eventName] = CapacitorUpdater.addListener(eventName, callback)
            .then((handle) => {
                updaterListenerHandles[eventName] = handle;
            })
            .finally(() => {
                delete updaterListenerPromises[eventName];
            });
    }
    await updaterListenerPromises[eventName];
}

async function registerUpdaterListeners() {
    await addUpdaterListenerOnce('download', ({ percent, bundle, version }) => {
        if (!appIsActive || applyingBundle) return;
        markDownloadStarted(bundle?.version ?? version, percent);
        checkInProgress = true;
        showDownloadScreen(typeof percent === 'number' ? percent : 0);
    });

    await addUpdaterListenerOnce('downloadComplete', () => {
        if (!appIsActive || applyingBundle) return;
        markDownloadSettled();
        // updateAvailable is the authoritative terminal event and follows this
        // progress-complete notification.
    });

    await addUpdaterListenerOnce('updateAvailable', ({ bundle }) => {
        if (!appIsActive || applyingBundle) return;
        markDownloadSettled();
        checkInProgress = false;
        acceptingStartupCheckResult = false;
        updateCheckAttempt += 1;
        clearCheckWatchdog();

        if (!bundle?.id) {
            showBlockingUpdateError(
                'The required update was downloaded but could not be identified. Retry the update check.',
                new Error('Capgo updateAvailable event did not include a bundle id'),
                () => startMandatoryUpdateCheck(),
            );
            return;
        }

        void installBundle(bundle.id);
    });

    await addUpdaterListenerOnce('downloadFailed', (event) => {
        if (!appIsActive || applyingBundle) return;
        markDownloadSettled();
        showBlockingUpdateError(
            'The required update could not be downloaded. Check your connection and retry.',
            new Error(event?.message ?? 'Capgo reported that the update download failed'),
            () => startMandatoryUpdateCheck(),
        );
    });

    await addUpdaterListenerOnce('noNeedUpdate', () => {
        if (!appIsActive || applyingBundle || !checkInProgress) return;
        // Ignore terminal events from Capgo's implicit native startup work until
        // getNextBundle() has been checked and our authoritative check has begun.
        if (startupGatePending && !acceptingStartupCheckResult) return;
        void completeStartupGate();
    });
}

async function registerLifecycleListeners() {
    if (!appStateListenerHandle) {
        if (!appStateListenerPromise) {
            appStateListenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                appIsActive = isActive;
                if (isActive) {
                    if (checkInProgress) {
                        if (startupGatePending) acceptingStartupCheckResult = true;
                    } else {
                        void startMandatoryUpdateCheck({ showChecking: startupGatePending });
                    }
                } else {
                    acceptingStartupCheckResult = false;
                    reportDownloadAbortIfInFlight('app_backgrounded');
                    // Keep the logical check serialized while backgrounded. On
                    // resume, Capgo may continue it and its eventual terminal
                    // event remains authoritative.
                    clearCheckWatchdog();
                }
            }).then((handle) => {
                appStateListenerHandle = handle;
            }).finally(() => {
                appStateListenerPromise = null;
            });
        }
        await appStateListenerPromise;
    }

    if (!pageHideListenerRegistered) {
        window.addEventListener('pagehide', () => {
            appIsActive = false;
            acceptingStartupCheckResult = false;
            reportDownloadAbortIfInFlight('page_hidden');
        });
        pageHideListenerRegistered = true;
    }
}

async function startMandatoryUpdateCheck({ showChecking = true } = {}) {
    if (!appIsActive || applyingBundle || checkInProgress || startupGateCompleting) return;

    // We just installed and rebooted into an update moments ago: skip hitting
    // the server again and resolve exactly as a noNeedUpdate check would.
    if (recentlyInstalledUpdate()) {
        if (startupGatePending) {
            void completeStartupGate();
        }
        return;
    }

    const attempt = ++updateCheckAttempt;
    checkInProgress = true;
    if (startupGatePending) acceptingStartupCheckResult = true;
    if (showChecking) showCheckingScreen();

    try {
        await CapacitorUpdater.triggerUpdateCheck();
    } catch (error) {
        if (attempt !== updateCheckAttempt) return;
        acceptingStartupCheckResult = false;
        showBlockingUpdateError(
            'Unable to check for required updates. Check your connection and retry.',
            error,
            () => startMandatoryUpdateCheck(),
        );
    }
}

async function initializeMandatoryUpdateGate() {
    if (!startupGatePending) return;

    const attempt = ++initializationAttempt;
    showCheckingScreen();
    armCheckWatchdog(
        () => initializeMandatoryUpdateGate(),
        () => {
            // Retire this attempt before exposing Retry. Native promises cannot be
            // cancelled, so every await below verifies the generation before it
            // is allowed to install a bundle or start the authoritative check.
            if (initializationAttempt === attempt) initializationAttempt += 1;
        },
    );

    try {
        // Start notifyAppReady immediately and release the retained native splash
        // independently. A transient splash-plugin failure must not delay updater
        // verification or make its eventual Retry state depend on a rejected call.
        const notifyReadyPromise = notifyCurrentBundleReady();
        void revealUpdateScreen();
        await notifyReadyPromise;
        if (attempt !== initializationAttempt || !startupGatePending) return;

        // Check staged state before registering event listeners. That prevents an
        // implicit noNeedUpdate event from resolving the gate while this mandatory
        // staged-bundle lookup is still pending.
        const next = await CapacitorUpdater.getNextBundle();
        if (attempt !== initializationAttempt || !startupGatePending) return;
        if (next?.id) {
            await installBundle(next.id, { stage: false });
            return;
        }

        await registerUpdaterListeners();
        if (attempt !== initializationAttempt || !startupGatePending) return;
        await registerLifecycleListeners();
        if (attempt !== initializationAttempt || !startupGatePending) return;

        // Initialization is complete. From this point the single authoritative
        // Capgo check owns the gate and has no client timeout/replacement race.
        clearCheckWatchdog();
        checkInProgress = false;
        acceptingStartupCheckResult = true;
        await startMandatoryUpdateCheck();
    } catch (error) {
        if (attempt !== initializationAttempt || !startupGatePending) return;
        acceptingStartupCheckResult = false;
        showBlockingUpdateError(
            'Unable to initialize required updates. Check your connection and retry.',
            error,
            () => initializeMandatoryUpdateGate(),
        );
    }
}

export const liveUpdates = {
    // Signals Capgo that the current bundle booted successfully. Exposed for
    // diagnostics; normal startup should use requireLatestVersion(), which calls
    // this before performing any network check.
    async notifyReady() {
        if (!isNative) return;
        await notifyCurrentBundleReady();
    },

    // Mandatory native startup gate. It resolves only when Capgo conclusively
    // reports noNeedUpdate. Available bundles are staged and reloaded; failures
    // and stalls remain blocked behind a Retry screen. Web resolves immediately.
    requireLatestVersion() {
        if (!isNative) return Promise.resolve();
        if (startupGatePromise) return startupGatePromise;

        startupGatePending = true;
        showCheckingScreen();
        startupGatePromise = new Promise((resolve) => {
            resolveStartupGate = resolve;
        });

        void initializeMandatoryUpdateGate();
        return startupGatePromise;
    },

    // Removes the raw-DOM "Checking for updates…" overlay. requireLatestVersion()
    // deliberately leaves this overlay in place when it resolves, so that Vue's
    // own "Connecting…" overlay (App.vue's showConnectingOverlay) is guaranteed
    // to already be painted underneath before this is called — otherwise there
    // is a blank gap between the two overlays while app.mount() and Vue's first
    // render run. Call this only after app.mount('#app') AND a paint tick.
    hideStartupOverlay() {
        hideUpdateScreen();
    },
};
